/**
 * Stay date range + per-day port times for City Planning — mirrors iOS
 * CruiseCityStayGrouping + DayPlanData arrival/departure flags.
 */

import type { Trip, TripCityVisitMeta } from '../types/client';
import { cruiseCityStayContaining } from './cruiseCityStayGrouping';
import { enumerateVisitDates } from './tourItemSave';
import { addDaysYmd } from './wallClock';

export interface StayDayPortTimes {
  dayDate: string;
  hasArrival: boolean;
  arrivalHm: string | null;
  hasDeparture: boolean;
  departureHm: string | null;
}

/** Inclusive civil days for the city stay (cruise consecutive ports grouped). */
export function stayDatesForCityVisit(
  trip: Trip,
  cityVisitId: string
): string[] {
  const meta = trip.cityVisitMeta?.[cityVisitId];
  const stay = cruiseCityStayContaining(trip, cityVisitId);
  const visits = stay.visits;

  if (visits.length === 0) {
    const fallback =
      meta?.arrivalDate || trip.startDate || new Date().toISOString().slice(0, 10);
    return enumerateVisitDates(meta?.arrivalDate, meta?.departureDate, fallback);
  }

  if (trip.tripType === 'cruise' && visits.length > 1) {
    const firstId = visits[0].id;
    const lastId = visits[visits.length - 1].id;
    const firstMeta = trip.cityVisitMeta?.[firstId];
    const lastMeta = trip.cityVisitMeta?.[lastId];
    const start =
      firstMeta?.arrivalDate || visits[0].arrivalDate || meta?.arrivalDate || '';
    const end =
      lastMeta?.departureDate ||
      lastMeta?.arrivalDate ||
      visits[visits.length - 1].arrivalDate ||
      start;
    if (!start) return [];
    return enumerateVisitDates(start, end, start);
  }

  const fallback =
    meta?.arrivalDate || trip.startDate || new Date().toISOString().slice(0, 10);
  return enumerateVisitDates(meta?.arrivalDate, meta?.departureDate, fallback);
}

/** Stay arrival / departure civil dates (inclusive). */
export function stayArrivalDeparture(
  trip: Trip,
  cityVisitId: string
): { arrivalDate: string; departureDate: string } {
  const dates = stayDatesForCityVisit(trip, cityVisitId);
  const meta = trip.cityVisitMeta?.[cityVisitId];
  const arrival = dates[0] || meta?.arrivalDate || '';
  const departure = dates[dates.length - 1] || meta?.departureDate || arrival;
  return { arrivalDate: arrival, departureDate: departure };
}

function metaForDay(
  trip: Trip,
  cityVisitId: string,
  dayDate: string
): TripCityVisitMeta | undefined {
  const stay = cruiseCityStayContaining(trip, cityVisitId);
  if (trip.tripType === 'cruise' && stay.visits.length > 1) {
    const match = stay.visits.find((v) => v.arrivalDate === dayDate);
    if (match) return trip.cityVisitMeta?.[match.id];
  }
  return trip.cityVisitMeta?.[cityVisitId];
}

/**
 * Port arrival/departure flags for one civil day inside a stay.
 * First day: arrival only (multi-day). Last day: departure only. Single day: both.
 */
export function portTimesForStayDay(
  trip: Trip,
  cityVisitId: string,
  dayDate: string
): StayDayPortTimes {
  const { arrivalDate, departureDate } = stayArrivalDeparture(trip, cityVisitId);
  const meta = metaForDay(trip, cityVisitId, dayDate) ?? trip.cityVisitMeta?.[cityVisitId];
  const multiDay = arrivalDate !== departureDate && Boolean(arrivalDate && departureDate);

  if (!multiDay) {
    return {
      dayDate,
      hasArrival: Boolean(meta?.portArrivalHm),
      arrivalHm: meta?.portArrivalHm ?? null,
      hasDeparture: Boolean(meta?.portDepartureHm),
      departureHm: meta?.portDepartureHm ?? null,
    };
  }

  const isFirst = dayDate === arrivalDate;
  const isLast = dayDate === departureDate;

  return {
    dayDate,
    hasArrival: isFirst && Boolean(meta?.portArrivalHm || trip.cityVisitMeta?.[cityVisitId]?.portArrivalHm),
    arrivalHm: isFirst
      ? meta?.portArrivalHm ?? trip.cityVisitMeta?.[cityVisitId]?.portArrivalHm ?? null
      : null,
    hasDeparture:
      isLast &&
      Boolean(meta?.portDepartureHm || trip.cityVisitMeta?.[cityVisitId]?.portDepartureHm),
    departureHm: isLast
      ? meta?.portDepartureHm ?? trip.cityVisitMeta?.[cityVisitId]?.portDepartureHm ?? null
      : null,
  };
}

/** Inclusive day count for stay duration label. */
export function stayDurationDays(arrivalDate: string, departureDate: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(arrivalDate)) return 1;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(departureDate) || departureDate < arrivalDate) {
    return 1;
  }
  let count = 0;
  let current = arrivalDate;
  let guard = 0;
  while (current <= departureDate && guard < 400) {
    count += 1;
    current = addDaysYmd(current, 1);
    guard += 1;
  }
  return Math.max(count, 1);
}
